import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Chip,
  Skeleton,
  styled,
  TableSortLabel,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { CheckIcon, IconTask, TimeIcon } from '../dashboard-icon';
import { FiberManualRecord } from '@mui/icons-material';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px',
    fontSize: 11,
  },
}));

interface PatientListTableProps {
  items?: any;
  handleOpenDrawer: any;
  handleSortRequest: any;
  orderBy: any;
  order: any;
}

const ListingTable: FC<PatientListTableProps> = (props) => {
  const { items = [], handleOpenDrawer, handleSortRequest, orderBy, order } = props;
  const getDashboardPatientList = useSelector((state: any) => state.getDashboardPatientReducer);

  const statusStyleProps: any = {
    IN_PROGRESS: {
      marginLeft: '10px',
      color: '#6366F1',
      backgroundColor: '#EBEEFE',
    },
    NOT_STARTED: {
      marginLeft: '10px',
      color: '#6C737F',
      backgroundColor: 'rgba(17, 25, 39, 0.08)',
    },
    FINALIZED: {
      marginLeft: '10px',
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
    },
    SIGNED: {
      marginLeft: '10px',
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
    },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            <TableCell>
              Patient Name{' '}
              <TableSortLabel
                active={orderBy === 'patientName'}
                direction={order}
                onClick={handleSortRequest}
              ></TableSortLabel>{' '}
            </TableCell>
            <TableCell>Task</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Status</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {getDashboardPatientList?.loading ? (
            <TableRow>
              <TableCell colSpan={6}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Box
                    textAlign="center"
                    key={index}
                    // p={3}
                  >
                    <Typography
                      variant="h4"
                      color="initial"
                    >
                      <Skeleton animation="wave" />
                    </Typography>
                  </Box>
                ))}
              </TableCell>
            </TableRow>
          ) : !!items.length ? (
            items?.map((patient: any) => {
              return (
                <TableRow
                  hover
                  key={patient?.uuid}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleOpenDrawer(patient)}
                >
                  <TableCell>
                    <Stack
                      alignItems="center"
                      direction="row"
                      gap="1rem"
                    >
                      <Avatar
                        src={`data:image/png;base64,${patient?.profileImage}`}
                        sx={{
                          height: 42,
                          width: 42,
                        }}
                      >
                        {`${patient?.lastName?.toUpperCase()?.at(0)}${patient?.firstName
                          ?.toUpperCase()
                          ?.at(0)}`}
                        {/* {getInitials(`${patient?.lastName} ${patient?.firstName}`)} */}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">{`${patient?.lastName}, ${patient?.firstName}`}</Typography>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="caption"
                        >
                          {patient?.patient_term}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      textTransform: 'capitalize',
                    }}
                  >
                    <Stack
                      direction={'row'}
                      gap={1}
                      alignItems={'center'}
                      textAlign={'center'}
                    >
                      <Box sx={{ width: '16px' }}>
                        {patient?.notes ? (
                          <FiberManualRecord
                            sx={{ fontSize: '12px', color: '#9747FF' }}
                            color="primary"
                          />
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {patient?.taskNameWithId?.map((taskName: any) => (
                          <span title={taskName?.value}>
                            <IconTask iconType={taskName.key} />
                            {/* <Typography sx={{ fontSize: '14px' }}>{taskName?.value}</Typography> */}
                          </span>
                        ))}
                        {patient?.taskNameWithId?.length === 1 ? (
                          <Typography variant="body1">
                            {patient?.taskNameWithId[0]?.value}
                          </Typography>
                        ) : (
                          patient?.taskNameWithId?.length > 1 && (
                            <LightTooltip
                              title={patient?.taskNameWithId?.map((group: any) => (
                                <Typography variant="caption3Text">{group?.value}</Typography>
                              ))}
                            >
                              <Typography variant="body1">
                                {patient?.taskNameWithId[0]?.value}+1
                              </Typography>
                            </LightTooltip>
                          )
                        )}

                        <Box>
                          {patient?.therapyNames?.map((group: any) => {
                            return (
                              <Typography
                                sx={{
                                  ml: 1,
                                  color:
                                    group?.charAt(0) === 'G'
                                      ? '#06AED4'
                                      : group?.charAt(0) === 'C'
                                      ? '#F79009'
                                      : null,
                                }}
                                variant="caption"
                                noWrap
                                key={group}
                              >
                                {group}
                              </Typography>
                            );
                          })}
                        </Box>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TimeIcon />
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {patient?.taskMinutes}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {patient?.status !== 'SIGNED' ? (
                      <Chip
                        label={
                          <Typography variant="overline">
                            {patient?.status === 'NOT_STARTED' || !patient?.status
                              ? 'Incomplete'
                              : patient?.status
                                  ?.split('_')
                                  .map(
                                    (item: string) =>
                                      item.slice(0, 1) + item.toLowerCase().slice(1, item.length)
                                  )
                                  .join(' ')}
                          </Typography>
                        }
                        variant="outlined"
                        sx={statusStyleProps[patient?.status ? patient?.status : 'NOT_STARTED']}
                      />
                    ) : (
                      <Chip
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CheckIcon />
                            <Typography variant="overline">Signed</Typography>
                          </Box>
                        }
                        variant="outlined"
                        sx={{
                          marginLeft: '10px',
                          color: '#107569',
                          backgroundColor: 'rgba(21, 183, 158, 0.12)',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ cursor: 'pointer' }}
                  >
                    <ArrowForwardIcon sx={{ color: '#6C737F' }} />
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={5}
                align="center"
              >
                <Typography
                  variant="h6"
                  sx={{ margin: '2.5rem auto' }}
                >
                  No Records Found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ListingTable;
